import { ChangeDetectionStrategy, Component, computed, inject, output } from '@angular/core';

import type { IconAvatar } from '@evc/web-components';
import { AvatarComponent } from '@evc/web-components';

import { AuthService } from '../../clients/core/auth/service/auth.service';
import { OrganizationsService } from '../../clients/core/organizations/organizations.service';
import { UserService } from '../../clients/core/user/user.service';
import { CoreClientUtilService } from '../../clients/core/utils/core-client-utils.service';
import { PlatformConfigService } from '../../features/config/config.service';
import { I18nPlatformService } from '../../features/i18n/i18n.service';
import { PlatformTranslatePipe } from '../../features/i18n/platform-translate.pipe';
import type { Guid } from '../../features/types/api-objects.type';

@Component({
  selector: 'evc-auth-organization-selection-modal',
  imports: [AvatarComponent, PlatformTranslatePipe],
  templateUrl: './auth-organization-selection-modal.html',
  styleUrl: './auth-organization-selection-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthOrganizationSelectionModalComponent {
  readonly #userService = inject(UserService);
  readonly #organizationsService = inject(OrganizationsService);
  readonly #coreClientUtilService = inject(CoreClientUtilService);
  readonly #configService = inject(PlatformConfigService);
  readonly #i18n = inject(I18nPlatformService);
  readonly #authService = inject(AuthService);

  readonly orgSelected = output<Guid>();
  readonly useAnotherAccount = output();

  readonly userProfile = this.#userService.profile.bind(this.#userService);

  readonly #forceLicense = !!this.#configService.get('auth')?.forceLicense;
  readonly #appTitle = computed(() => {
    const { title, name } = this.#configService.getComputed('app')() ?? {};
    return title ?? name ?? '';
  });

  readonly welcome = computed(() => {
    const displayName = this.#userService.profile()?.givenName ?? '';

    return this.#i18n.t('auth-organization-selection-modal.welcome', { displayName });
  });

  readonly organizations = computed(() =>
    this.#organizationsService.entries().map((org) => {
      const badge = this.#coreClientUtilService.formatToBadgeItemType(org);
      const hasRoles = (org.userRoles?.length ?? 0) > 0;
      const hasLicense = !this.#forceLicense || org.hasLicenseForApplication !== false;
      const hasAccess = hasRoles && hasLicense;

      return {
        ...badge,
        hasAccess,
        additionalText:
          this.#forceLicense && org.hasLicenseForApplication === false
            ? this.#i18n.t('auth-organization-selection-modal.no_license', {
                appName: this.#appTitle(),
              })
            : badge.additionalText,
      };
    }),
  );

  readonly useAnotherAccountAvatar: IconAvatar = {
    type: 'icon',
    icon: 'profile',
    color: 'var(--content-accent)',
  };

  readonly logoutAvatar: IconAvatar = {
    type: 'icon',
    icon: 'log-out',
    color: 'var(--content-accent)',
  };

  readonly logout = this.#authService.logout.bind(this.#authService);
}
